import { useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { PiWarningDiamondFill } from 'react-icons/pi';
import { peso, TOPUP_MINIMUM } from './Credits';

/**
 * Announces the switch to credits: remote accounts are no longer paid by
 * e-wallet directly, customers top up credits (at least ₱100) and spend them.
 * Shown as a dismissible popup on the Remote Accounts page and as a fixed
 * notice on the top-up page.
 */

const monthly = 40;
const yearly = 360;

/** Bump the version to show the popup again to everyone after the wording changes. */
const HIDDEN_KEY = 'paymentNotice.v1.hidden';

/** Per user, so another account signed in on the same browser still sees it once. */
function hiddenKey() {
    let id = '';
    try {
        id = window.localStorage.getItem('id') ?? '';
    } catch (error) { }
    return `${HIDDEN_KEY}.${id}`;
}

function isHidden() {
    try {
        return window.localStorage.getItem(hiddenKey()) === '1';
    } catch (error) {
        return false;
    }
}

function NoticePoints() {
    return (
        <>
            <p className='mt-1'>
                Our payment provider now adds extra charges to every transaction. To keep our prices the same,
                we no longer charge each purchase or renewal separately. Instead:
            </p>
            <ul className='mt-2 list-disc space-y-1 pl-5'>
                <li>Remote accounts are now paid with <b>credits</b> (1 credit = ₱1).</li>
                <li>Top up credits with QR Ph (any bank or e-wallet app), GCash, Maya, ShopeePay or GrabPay. The <b>minimum top-up is {peso(TOPUP_MINIMUM)}</b>, with no service fee.</li>
                <li>Buying or renewing uses your credits: 1 month = {monthly} credits, 1 year = {yearly} credits.</li>
            </ul>
        </>
    );
}

/** The fixed notice at the top of the top-up page. */
export function PaymentNoticeBanner({ className }: { className?: string }) {
    return (
        <div className={`flex flex-row gap-3 rounded-2xl bg-amber-50 p-4 text-sm leading-relaxed text-amber-900 ring-1 ring-amber-200 sm:p-5 ${className ?? ''}`}>
            <PiWarningDiamondFill size={22} className='mt-0.5 shrink-0 text-amber-500' />
            <div className='min-w-0'>
                <div className='font-bold'>Our payment method has changed</div>
                <NoticePoints />
            </div>
        </div>
    );
}

/** The popup on the Remote Accounts page, until the user ticks "Don't show this again". */
export function PaymentNoticeDialog() {

    const [open, setOpen] = useState(false);
    const [dontShow, setDontShow] = useState(false);

    useEffect(() => {
        setOpen(!isHidden());
    }, []);

    function close() {
        if (dontShow) {
            try {
                window.localStorage.setItem(hiddenKey(), '1');
            } catch (error) { }
        }
        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={close} className="relative z-[60]">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in" />

            <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="w-full max-w-md rounded-2xl bg-white p-6 text-left shadow-2xl ring-1 ring-slate-200 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in">

                        <div className='flex flex-row items-start gap-3'>
                            <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 ring-1 ring-amber-200'>
                                <PiWarningDiamondFill size={24} className='text-amber-500' />
                            </span>
                            <div className='min-w-0'>
                                <DialogTitle as="h2" className="text-lg font-bold tracking-tight text-slate-900">Our payment method has changed</DialogTitle>
                                <div className='text-sm leading-relaxed text-slate-600'>
                                    <NoticePoints />
                                </div>
                            </div>
                        </div>

                        <label className='mt-5 flex cursor-pointer flex-row items-center gap-2 text-sm text-slate-600'>
                            <input
                                type='checkbox'
                                checked={dontShow}
                                onChange={(e) => setDontShow(e.target.checked)}
                                className='h-4 w-4 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500' />
                            Don't show this again
                        </label>

                        <div className='mt-5 flex flex-row justify-end gap-2'>
                            <button
                                type='button'
                                onClick={close}
                                className='inline-flex min-h-[40px] items-center rounded-full bg-white px-5 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200 transition hover:bg-slate-50'>
                                Got it
                            </button>
                            <a
                                href='/credits'
                                onClick={close}
                                className='inline-flex min-h-[40px] items-center rounded-full bg-emerald-600 px-5 text-sm font-bold text-white transition hover:bg-slate-950'>
                                Top up credits
                            </a>
                        </div>

                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
